import { colors } from '@globals/themes/defaultTheme'
import {
  desktopQuery,
  mobileContainerQuery,
  spacing,
  tabletQuery,
} from '@globals/themes/themeConstants'
import { MediaObjectUrl } from '@kudos/http-client'
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Skeleton from '@mui/material/Skeleton'
import { SxProps, Theme } from '@mui/material/styles'
import { FC, useEffect, useState } from 'react'

export const skeletonStyle = (loading: boolean) => ({
  display: loading ? 'block' : 'none',
  borderRadius: spacing(1.5),
  [desktopQuery]: {
    height: '100%',
    aspectRatio: '1 / 1',
  },
  [tabletQuery]: {
    height: '100%',
    width: '100%',
  },
  [mobileContainerQuery]: {
    height: '100%',
    width: '100%',
  },
})

export const paperImgStyle: SxProps = {
  boxShadow: 'none',
  backgroundColor: colors.grayUltraLight,
  border: 'none',
}

type PaperImageProps = {
  alt?: string
  imgSrc?: string
  mediaUrls?: MediaObjectUrl | null
  defaultImgSrc?: string
  objectFit?: 'fill' | 'contain' | 'cover' | 'none' | 'scale-down'
  sx?: SxProps<Theme>
  maxWidth?: string | number
  maxHeight?: string | number
  backgroundColor?: string
  borderRadius?: number | string
  height?: 'auto' | '100%' | number
}
const MediaImage: FC<PaperImageProps> = ({
  alt,
  imgSrc,
  mediaUrls,
  defaultImgSrc,
  maxWidth,
  maxHeight,
  objectFit = 'cover',
  backgroundColor = colors.grayUltraLight,
  borderRadius = 0,
  height = 'auto',
}) => {
  const [loading, setLoading] = useState<boolean>(true)
  const [src, setSrc] = useState<string>(imgSrc ?? defaultImgSrc ?? '')

  useEffect(() => {
    setSrc(imgSrc ?? defaultImgSrc ?? '')
  }, [imgSrc, defaultImgSrc])
  return (
    // this box adds the necessary extra width when the logo chosen
    // is smaller than expected. it will fill the extra space for layout
    // purposes (which the img component won't do)
    // see myTenants/index.tsx for an example of where this is required.
    <Box maxWidth={maxWidth} maxHeight={maxHeight} height={height}>
      <Skeleton
        animation="wave"
        variant="rounded"
        sx={{ ...skeletonStyle(loading), width: '100%', height: '100%' }}
      />
      <Paper
        component="img"
        alt={alt}
        src={src}
        sx={{
          ...paperImgStyle,
          objectFit,
          backgroundColor,
          display: loading ? 'none' : 'unset',
          width: '100%',
          height: '100%',
          borderRadius,
        }}
        onLoad={() => setLoading(false)}
        // Note falling back to using the original url if
        // the variant is not available yet.
        onError={() => imgSrc && mediaUrls && setSrc(mediaUrls.url)}
      />
    </Box>
  )
}

export default MediaImage
